/**
 * WorkerSubRow — a compact sub-row that renders directly under a master thread
 * row when the master is expanded (M4.1).
 *
 * Layout: indented 28 px (pl-7), text-[10px], muted opacity tiers per spec.
 * Click navigates to the worker thread directly.
 */

import { memo } from "react";
import { SidebarMenuSubButton, SidebarMenuSubItem } from "../ui/sidebar";
import type { ThreadStatus, WorkerRole } from "@cmmd-forge/contracts";
import { formatWorkerRole } from "../workbar/WorkerRoleBadge";

// ---------------------------------------------------------------------------
// Status chip label (text-only — no icons, keeps it compact)
// ---------------------------------------------------------------------------

const STATUS_LABELS: Readonly<Record<ThreadStatus, string>> = {
  idle: "idle",
  running: "running",
  stuck: "stuck",
  "needs-review": "review",
  ready: "ready",
  merged: "merged",
  conflicted: "conflict",
  archived: "archived",
};

function statusChipClass(status: ThreadStatus): string {
  if (status === "stuck" || status === "needs-review" || status === "conflicted") {
    return "text-warning-foreground";
  }
  if (status === "running") return "text-success-foreground";
  if (status === "merged") return "text-info-foreground";
  return "text-muted-foreground/40";
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WorkerSubRowProps {
  readonly id: string;
  readonly title: string;
  readonly status: ThreadStatus;
  readonly workerRole?: WorkerRole | null;
  readonly onNavigate: (workerId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkerSubRow = memo(function WorkerSubRow({
  id,
  title,
  status,
  workerRole,
  onNavigate,
}: WorkerSubRowProps) {
  const buttonRender = <button type="button" />;
  const role = formatWorkerRole(workerRole);

  return (
    <SidebarMenuSubItem className="w-full" data-worker-sub-row>
      <SidebarMenuSubButton
        render={buttonRender}
        size="sm"
        className="h-5 w-full translate-x-0 pl-7 text-left text-[10px] text-muted-foreground/60 hover:bg-accent hover:text-muted-foreground"
        onClick={() => onNavigate(id)}
        data-testid={`worker-sub-row-${id}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span
            className={`shrink-0 rounded-sm px-1 text-[9px] font-medium uppercase ${role.tone}`}
          >
            {role.label}
          </span>
          <span className="min-w-0 flex-1 truncate">{title}</span>
          <span className={`shrink-0 ${statusChipClass(status)} text-[10px]`}>
            {STATUS_LABELS[status]}
          </span>
        </span>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
});
